import {WebConferenceRepository} from "@api/repositories/WebConferenceRepository";
import {WebConferenceJoinResult} from "@api/models/web-conferences/WebConferenceJoinResult";
import {Meeting} from "@api/models/meetings/Meeting";
import {Element} from "@api/models/Element";
import Vue from "vue";
import {UserRoleType} from "@share/web-conferences/enums/UserRoleType";
import {AxiosError} from "axios";
import {BackendErrorInterface} from "@api/models/interfaces/BackendError";
import {WebConferenceErrorCode} from "@api/enums/errors/WebConference";
import {WebConferenceError} from "@api/models/web-conferences/WebConferenceError";
import {CurrentPresentation} from "@api/models/interfaces/web-conference/CurrentPresentation";
import {fireSharingChanged} from "@share/web-conferences/helpers/EventBusHelper";
import {isCurrentPresentationChanged} from "@share/web-conferences/helpers/slides/CurrentPresnetationComporator";
import {clearTimeoutForCurrentPresentation} from "@share/web-conferences/helpers/slides/CurrentPresentationAbsenceHandler";
import {GlobalEvents, GlobalEventTypes} from "@share/web-conferences/helpers/GlobalEvents";
import {StoreHelper} from "@share/web-conferences/helpers/StoreHelper";
import { loggerInstance } from "@share/live-kit/logger";
// import {setWebConferenceDataAction} from "@share/web-conferences/store/actions";
// import {WebConferenceJoinResultInterface} from "@api/models/interfaces/web-conference/WebConferenceJoinResultInterface";

export class WebConferenceService {
    webConferenceData?: WebConferenceJoinResult | null = Vue.observable(null);
    handIsRaised = false;

    constructor(
        public readonly webConferenceRepository: WebConferenceRepository,
        private readonly _schemaId: string,
        private readonly _element: Meeting | Element,
        private readonly storeHelper: StoreHelper,
        private readonly globalEvents: GlobalEvents
    ) {
    }

    get schemaId() {
        return this._schemaId;
    }

    get objectId() {
        return this._element.id;
    }

    get element() {
        return this._element.id;
    }

    async join(): Promise<WebConferenceJoinResult | WebConferenceError | null> {
        let conferenceData: WebConferenceJoinResult | null = null;
        loggerInstance.log('joining to appercode:' + this._schemaId + ',' + this._element.id);

        try {
            conferenceData = await this.webConferenceRepository.join(this._schemaId, this._element.id);
        }
        catch (e) {

            const { response } = e as AxiosError<BackendErrorInterface>;

            loggerInstance.log('failed to join, ' + response?.data.message);

            if (WebConferenceError.isWebconferenceError(response?.data.code || "")) {
                return new WebConferenceError(response?.data.code as WebConferenceErrorCode);
            }
            else {
                conferenceData = null;
                this.storeHelper.store.dispatch('reportError', e);
            }
        }

        Vue.set(this, 'webConferenceData', conferenceData);

        this.storeHelper.setWebConferenceData(conferenceData as WebConferenceJoinResult);

        this.fireSharingChanged();

        return conferenceData;
    }

    /**
     * identifies whether web conference data is not empty
     */
    isWebConferenceDataNotEmpty(): boolean {
        return Boolean(this.webConferenceData && this.webConferenceData.userRole);
    }

    /**
     * Returns agora credentials
     */
    get agoraCredentials() {
        return this.webConferenceData?.agoraCredentials;
    }

    /**
     * Returns live kit credentials
     */
    get liveKitCredentials() {
        return this.webConferenceData?.liveKitCredentials;
    }

    setNewRole(newRole: UserRoleType) {
        if (this.webConferenceData) {
            this.webConferenceData.userRole = newRole;
        }
    }

    async raiseHand(): Promise<boolean> {
        try {
            await this.webConferenceRepository.raiseHand(this._schemaId, this._element.id);
            this.handIsRaised = true;
            return true;
        }
        catch (e) {
            return false;
        }
    }

    async lowerHand(): Promise<boolean> {
        try {
            await this.webConferenceRepository.lowerHand(this._schemaId, this._element.id);
            this.handIsRaised = false;
            return true;
        }
        catch (e) {
            return false;
        }
    }

    async lowerHandIfRaised() {
        if (this.handIsRaised) {
            await this.lowerHand();
        }
    }

    async changeCurrentPresentation(data: CurrentPresentation | null) {
        try {
            await this.webConferenceRepository.changePresentation(this._schemaId, this._element.id, data);
            // const newPresentation = await this.webConferenceRepository.changePresentation(this._schemaId, this._element.id, data);
            clearTimeoutForCurrentPresentation();
            // this.setCurrentPresentation(newPresentation);
            return true;
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportError', e);
            return false;
        }
    }

    async changeSlide(fileId: string) {
        const newPresentationData = {...this.webConferenceData?.currentPresentation} as CurrentPresentation;
        newPresentationData.data.currentSlideFileId = fileId;
        await this.changeCurrentPresentation(newPresentationData);
    }

    async nullCurrentPresentation() {
        await this.changeCurrentPresentation(null);
    }

    setCurrentPresentation(newCurrentPresentation: CurrentPresentation | null, fireEvent = true) {
        if (this.webConferenceData && isCurrentPresentationChanged(newCurrentPresentation, this.webConferenceData.currentPresentation)) {
            // this.webConferenceData.currentPresentation = newCurrentPresentation;
            Vue.set(this.webConferenceData, 'currentPresentation', newCurrentPresentation);

            fireEvent && this.fireSharingChanged();

            this.globalEvents.notify(
                this.webConferenceData.currentPresentation
                ? GlobalEventTypes.LowerVideoQuality
                : GlobalEventTypes.RaiseVideoQuality
            );
        }
    }

    fireSharingChanged() {
        if (this.webConferenceData) {
            fireSharingChanged(this.schemaId, this.objectId, this.webConferenceData?.currentPresentation);
        }
    }
}
